import { memo, ReactNode, useCallback } from 'react';
import { useTranslate } from '@tolgee/react';
import { Formik, FormikConfig, FormikHelpers, FormikProps } from 'formik';
import { Form, Input } from 'antd';

import FormField from 'components/FormField';
import { useConversationToggleTextareaProps } from 'hooks/business/conversationsHooks/useConversationHotKey';
import { sellerMemosLimit } from './constants';

export type TradeCommentFormValues = Pick<
  Conversation.Trade.Vo,
  'internalTradeId' | 'sellerMemos' | 'editSellerMemoType'
> & {
  platform: General.PlatformUpperCaseTunnel;
};

type FV = TradeCommentFormValues;

export type TradeCommentFormHelpers = FormikHelpers<FV>;

type FormRenderChildren<FV> = (nodes: ReactNode, formProps: FormikProps<FV>) => ReactNode;

export interface TradeCommentFormProps extends FormikConfig<FV> {
  children?: ReactNode | FormRenderChildren<FV>;
}

function TradeCommentForm(props: TradeCommentFormProps) {
  const { initialValues, children, ...otherProps } = props;

  const isRenderProps = typeof children === 'function';

  const { t } = useTranslate();

  const validate = useCallback(
    (values: FV) => {
      const errors: Partial<Record<keyof typeof values, any>> = {};

      const { sellerMemos } = values;

      if (sellerMemos && sellerMemos.length > sellerMemosLimit) {
        errors.sellerMemos = t('trade.commentValidateLimit', { limit: sellerMemosLimit });
      }

      return errors;
    },
    [t],
  );

  const conversationToggleTextAreaProps = useConversationToggleTextareaProps();

  return (
    <Formik enableReinitialize initialValues={initialValues} validate={validate} {...otherProps}>
      {(props) => {
        const form = (
          <Form>
            <FormField<FV['sellerMemos']> name="sellerMemos">
              {({ field: { onBlur, ...others } }) => {
                return (
                  <Input.TextArea
                    placeholder={t('trade.commentPlaceholder')}
                    maxLength={sellerMemosLimit}
                    {...others}
                    {...conversationToggleTextAreaProps}
                    onBlur={() => {
                      onBlur();
                      conversationToggleTextAreaProps.onBlur();
                    }}
                  />
                );
              }}
            </FormField>
          </Form>
        );

        return isRenderProps ? (children as FormRenderChildren<FV>)(form, props) : form;
      }}
    </Formik>
  );
}

export default memo(TradeCommentForm);
